/**
 * Billing Service - Puppeteer-Interceptable Billing Flow
 *
 * Similar to authService, this provides a unified billing interface that works in:
 * 1. Normal browser (opens payment in default browser)
 * 2. Puppeteer/Electron environment (interceptable for external browser payment)
 */
export const billingService = {
  /**
   * Detects if running inside Puppeteer/Electron environment
   */
  isInPuppeteer: () => {
    return window.__EDUTOOLS_PUPPETEER__ === true;
  },

  /**
   * Main upgrade method - routes to appropriate payment flow
   * @param {string} planType - 'pro' or 'enterprise'
   * @param {string} billingCycle - 'monthly' or 'annual'
   * @returns {Promise<Object>} Payment result
   */
  upgradeToPlan: async (planType, billingCycle = 'monthly') => {
    console.log(`[BillingService] Upgrade request: ${planType} (${billingCycle})`);

    if (billingService.isInPuppeteer()) {
      console.log('[BillingService] Puppeteer environment detected - using interceptable flow');
      return await billingService.upgradeViaPuppeteer(planType, billingCycle);
    } else {
      console.log('[BillingService] Normal browser - opening payment in new window');
      return await billingService.upgradeViaPopup(planType, billingCycle);
    }
  },

  /**
   * Normal browser payment flow
   * Opens payment page in current window
   */
  upgradeViaPopup: async (planType, billingCycle) => {
    try {
      // Always redirect to pricing page
      const useLocalUrl = window.location.hostname === "localhost";
      const paymentUrl = useLocalUrl
        ? `http://localhost:3000/pricing`
        : `https://edutools.app/pricing`;

      window.location.href = paymentUrl;

      return { success: true };
    } catch (error) {
      console.error('[BillingService] Popup payment failed:', error);
      throw error;
    }
  },

  /**
   * Puppeteer-interceptable payment flow
   * Sets flags that Puppeteer monitors, then waits for Electron to resolve
   */
  upgradeViaPuppeteer: async (planType, billingCycle) => {
    return new Promise((resolve, reject) => {
      console.log('[BillingService] Setting up Puppeteer billing request');

      // Set flag for Electron to detect
      window.__EDUTOOLS_BILLING_REQUEST__ = {
        type: 'upgrade',
        planType: planType,
        billingCycle: billingCycle,
        timestamp: Date.now(),
      };

      // Store resolve/reject handlers globally so Electron can call them
      window.__EDUTOOLS_BILLING_RESOLVE__ = resolve;
      window.__EDUTOOLS_BILLING_REJECT__ = reject;

      // Set 60-second timeout
      const timeout = setTimeout(() => {
        console.error('[BillingService] Billing request timed out');
        reject(new Error('Billing timeout - please try again'));
        billingService.cleanupBillingRequest();
      }, 60000);

      window.__EDUTOOLS_BILLING_TIMEOUT__ = timeout;

      console.log('[BillingService] Waiting for Electron to handle billing...');
    });
  },

  /**
   * Cleanup function to remove billing flags and handlers
   */
  cleanupBillingRequest: () => {
    if (window.__EDUTOOLS_BILLING_TIMEOUT__) {
      clearTimeout(window.__EDUTOOLS_BILLING_TIMEOUT__);
    }
    window.__EDUTOOLS_BILLING_REQUEST__ = null;
    window.__EDUTOOLS_BILLING_RESOLVE__ = null;
    window.__EDUTOOLS_BILLING_REJECT__ = null;
  },

  /**
   * Called by Electron when billing completes (success or failure)
   * @param {Object} billingData - Result from Electron/payment provider
   */
  handleBillingComplete: async (billingData) => {
    try {
      console.log('[BillingService] Billing completion received from Electron', billingData);

      // Clear timeout
      if (window.__EDUTOOLS_BILLING_TIMEOUT__) {
        clearTimeout(window.__EDUTOOLS_BILLING_TIMEOUT__);
      }

      if (billingData.success === true) {
        console.log('[BillingService] Billing successful!');

        // Store billing state
        const billingState = {
          plan: billingData.plan || 'pro',
          billingCycle: billingData.billingCycle || 'monthly',
          timestamp: billingData.timestamp || Date.now(),
          status: 'active',
        };
        localStorage.setItem('edutools_billing_state', JSON.stringify(billingState));

        // Resolve the pending promise
        if (window.__EDUTOOLS_BILLING_RESOLVE__) {
          window.__EDUTOOLS_BILLING_RESOLVE__(billingData);
        }

        // Cleanup
        billingService.cleanupBillingRequest();

        return billingData;
      } else {
        // Billing failed
        const error = new Error(billingData.error || 'Payment failed');
        console.error('[BillingService] Billing failed:', error);

        if (window.__EDUTOOLS_BILLING_REJECT__) {
          window.__EDUTOOLS_BILLING_REJECT__(error);
        }

        billingService.cleanupBillingRequest();
        throw error;
      }
    } catch (error) {
      console.error('[BillingService] Error handling billing completion:', error);

      if (window.__EDUTOOLS_BILLING_REJECT__) {
        window.__EDUTOOLS_BILLING_REJECT__(error);
      }

      billingService.cleanupBillingRequest();
      throw error;
    }
  },

  /**
   * Get current billing status from localStorage
   */
  getBillingStatus: () => {
    try {
      const billingState = localStorage.getItem('edutools_billing_state');
      return billingState ? JSON.parse(billingState) : null;
    } catch (error) {
      console.error('[BillingService] Error reading billing state:', error);
      return null;
    }
  },
};

// Expose billing service globally for Electron to call
if (typeof window !== 'undefined') {
  window.billingService = billingService;
}
